import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AppLayout from '../../layouts/AppLayout';
import FeatureControlBar from '../../components/FeatureControlBar/FeatureControlBar';
import ReservatorioCard from './components/ReservatorioCard';
import DashboardSummary from './components/DashboardSummary';
import useMonitoramentoGlobal from '../../hooks/useMonitoramentoGlobal';
import useDadosCondominio from '../../hooks/useDadosCondominio';
import useAuth from '../../hooks/useAuth';
import useActiveCondominio from '../../hooks/useActiveCondominio';
import useReservatorioFetch from './hooks/useReservatorioFetch';
import ErrorPopup from '../../components/ErrorPopup/ErrorPopup';
import './styles/reservatorios.css';

const ReservatorioControl = () => {
    const { user } = useAuth();
    const { activeCondominio, setActiveCondominio, condominios } = useActiveCondominio();

    // Hook global para dados de condomínio (blocos) - sem apartamentos para otimizar payload
    const { blocos, error: errorDados, clearError: clearErrorDados } = useDadosCondominio({
        projection: 'reservatorios.v1'
    });

    const [selectedBloco, setSelectedBloco] = useState('');

    const apiUrl = process.env.REACT_APP_API_URL;

    // Hook customizado para fetch de reservatórios
    const { reservatorios, resumo, error: errorFetch, isLoading: isLoadingFetch, refetch, clearError: clearErrorFetch } = useReservatorioFetch(
        selectedBloco,
        activeCondominio,
        apiUrl
    );

    // Hook global para monitoramento de comunicação
    const { problemasReservatorios, isLoading, consultarProblemas } = useMonitoramentoGlobal(apiUrl, activeCondominio);

    // Consolidar erros (dados ou fetch)
    const error = errorDados || errorFetch;

    // ✨ FUTURO: Filtrar blocos que têm reservatórios
    // Por enquanto, mostrar todos os blocos (quando o usuário seleciona, a API retorna vazio se não tem módulos)
    // Implementar quando tivermos endpoint global que retorna quais blocos têm reservatórios
    const blocosComReservatorios = blocos;

    // Filtrar problemas globais para o banner (todos offline, status != 'online')
    const problemasGlobais = useMemo(() => {
        return (problemasReservatorios || []).filter(p => p.status !== 'online');
    }, [problemasReservatorios]);

    // Map de status de comunicação por módulo (chave única: modulo_id + channelnum)
    const statusComunicacao = useMemo(() => {
        const map = {};
        reservatorios.forEach(reservatorio => {
            // Procurar problema específico para este reservatório usando chave única (modulo_id + channelnum)
            const problema = (problemasReservatorios || []).find(
                p => p.modulo_id === reservatorio.modulo_id &&
                    p.channelnum === reservatorio.channelnum
            );
            if (problema) {
                map[reservatorio.id] = {
                    problema: true,
                    status: problema.status,
                    mensagem: problema.mensagem,
                    minutosOffline: problema.minutosOffline,
                    ultimaAtualizacao: problema.ultimaAtualizacao
                };
            } else {
                map[reservatorio.id] = {
                    problema: false,
                    status: 'online',
                    mensagem: 'Comunicação normal',
                    minutosOffline: 0
                };
            }
        });
        return map;
    }, [reservatorios, problemasReservatorios]);

    // Selecionar primeiro bloco automaticamente quando blocos mudam
    useEffect(() => {
        if (blocos.length > 0 && !selectedBloco) {
            setSelectedBloco(blocos[0].id);
        }
    }, [blocos, selectedBloco]);

    // Handler para mudança de condomínio
    const handleCondomínioChange = (novoCondominioId) => {
        setActiveCondominio(novoCondominioId);
        setSelectedBloco(''); // Reset bloco ao trocar condomínio
    };

    // Handler para atualizar tanto os dados dos reservatórios quanto o status de comunicação
    const handleRefresh = useCallback(() => {
        refetch();
        consultarProblemas();
    }, [refetch, consultarProblemas]);

    // Handler para fechar popup de erro
    const closeErrorPopup = () => {
        clearErrorDados();
        clearErrorFetch();
    };

    return (
        <AppLayout
            titulo="Monitoramento de Reservatórios"
            cargo={user?.cargo}
            condominios={condominios}
            activeCondominio={activeCondominio}
            onCondomínioChange={handleCondomínioChange}
            problemas={problemasGlobais}
            onRefresh={handleRefresh}
            isChecking={isLoading}
        >
            <div className="page-container">
                <FeatureControlBar
                    title="Monitoramento de Reservatórios"
                    blocos={blocosComReservatorios}
                    selectedBloco={selectedBloco}
                    onBlocoChange={setSelectedBloco}
                    onRefresh={handleRefresh}
                    isRefreshing={isLoadingFetch}
                />

                {!isLoadingFetch && reservatorios.length > 0 && resumo && (
                    <div className="reservatorios-section-group">
                        <h3 className="section-group-title">
                            Visão Geral - {blocosComReservatorios.find(b => parseInt(b.id) === parseInt(selectedBloco))?.nome || 'Bloco'}
                        </h3>
                        <DashboardSummary resumo={resumo} blocoId={selectedBloco} />
                    </div>
                )}
                
                <div className="reservatorios-section-group">
                    {reservatorios.length > 0 && (
                        <h3 className="section-group-title">Tanques</h3>
                    )}
                    <div className="reservatorios-section">
                        {reservatorios.length === 0 && !isLoadingFetch ? (
                            <p className="feature-section__empty" style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', padding: '40px' }}>
                                Nenhum reservatório encontrado para o bloco selecionado.
                            </p>
                        ) : (
                            reservatorios.map(reservatorio => (
                                <ReservatorioCard
                                    key={reservatorio.id}
                                    reservatorio={reservatorio}
                                    statusComm={statusComunicacao[reservatorio.id]}
                                    isLoading={isLoading}
                                />
                            ))
                        )}
                    </div>
                </div>

                <ErrorPopup error={error} onClose={closeErrorPopup} />
            </div>
        </AppLayout>
    );
};

export default ReservatorioControl;
